import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Grid,
  List,
  Edit,
  Trash2,
  Plus,
  Calendar,
  User,
  Download,
  Check,
  X
} from 'lucide-react';
import { templateStorageService, type Template } from '../services/templateStorageService';
import { supabase } from '../../../foundation/altzero_base/base/supabase/supabaseClient';
import ScopingAILayout from '../components/ScopingAILayout';
export default function Templates({ standalone = false }: { standalone?: boolean }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [user, setUser] = useState<any>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Get current user
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getCurrentUser();
  }, []);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!user) return;

        const result = await templateStorageService.getUserTemplates(user.id);
        
        if (result.success) {
          setTemplates(result.templates || []);
        } else {
          setError(result.error || 'Failed to load templates');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [user]);

  // Filter templates based on search query
  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (template.description && template.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Handle template edit
  const handleEditTemplate = async (template: Template) => {
    try {
      // Load template data
      const result = await templateStorageService.loadTemplate(template);
      
      if (result.success && result.data) {
        // Navigate to template editor with template data
        // We'll pass the template data via state
        navigate('/tender/template-editor', { 
          state: { 
            templateData: result.data,
            templateId: template.id 
          } 
        });
      } else {
        alert(`Failed to load template: ${result.error}`);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      alert('Failed to load template. Please try again.');
    }
  };

  // Handle template delete
  const handleDeleteTemplate = async (template: Template) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) {
      return;
    }

    try {
      const result = await templateStorageService.deleteTemplate(template.id, user.id);
      
      if (result.success) {
        setTemplates(prev => prev.filter(t => t.id !== template.id));
        alert('Template deleted successfully');
      } else {
        alert(`Failed to delete template: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert('Failed to delete template. Please try again.');
    }
  };

  // Handle inline rename
  const startEditing = (template: Template, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(template.id);
    setEditingName(template.name);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const saveRename = async (templateId: string) => {
    const trimmed = editingName.trim();
    if (!trimmed) { cancelEditing(); return; }

    const original = templates.find(t => t.id === templateId)?.name;
    if (trimmed === original) { cancelEditing(); return; }

    const { error } = await supabase
      .from('templates')
      .update({ name: trimmed, updated_at: new Date().toISOString() })
      .eq('id', templateId);

    if (!error) {
      setTemplates(prev => prev.map(t => t.id === templateId ? { ...t, name: trimmed } : t));
    }
    cancelEditing();
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent, templateId: string) => {
    if (e.key === 'Enter') saveRename(templateId);
    if (e.key === 'Escape') cancelEditing();
  };

  // Handle create new template (block-based editor)
  const handleCreateTemplate = () => {
    navigate('/tender/template-editor');
  };

  // Open the new HTML-based WYSIWYG template editor
  const handleCreateHtmlTemplate = () => {
    navigate('/tender/html-template-editor');
  };



  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  const content = (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
                <p className="text-sm text-gray-500">Manage your template library</p>
              </div>
            </div>
            
            {/* <button
              onClick={handleCreateHtmlTemplate}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              New HTML Template
            </button> */}
            <button
              onClick={handleCreateTemplate}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Block Editor
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and View Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredTemplates.length === 0 && !error && (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No templates found' : 'No templates yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : 'Create your first template to get started'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={handleCreateTemplate}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        )}

        {/* Templates Grid/List */}
        {!loading && filteredTemplates.length > 0 && (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
            : 'space-y-4'
          }>
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow ${
                  viewMode === 'list' ? 'flex items-center p-4' : 'p-6'
                }`}
              >
                {viewMode === 'grid' ? (
                  // Grid View
                  <>
                    {/* Template Preview */}
                    <div className="aspect-video bg-gray-100 rounded-lg mb-4 flex items-center justify-center">
                      {template.thumbnail_url ? (
                        <img 
                          src={template.thumbnail_url} 
                          alt={template.name}
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : (
                        <FileText className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                    
                    {/* Template Info */}
                    <div className="flex-1">
                      {editingId === template.id ? (
                        <div className="flex items-center gap-1 mb-1" onClick={e => e.stopPropagation()}>
                          <input
                            ref={editInputRef}
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => handleRenameKeyDown(e, template.id)}
                            className="flex-1 text-sm font-semibold border border-blue-400 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                          <button onClick={() => saveRename(template.id)} className="text-green-600 hover:text-green-700"><Check className="h-4 w-4" /></button>
                          <button onClick={cancelEditing} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                          <button onClick={e => startEditing(template, e)} title="Rename template" className="text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"><Edit className="h-3 w-3" /></button>
                        </div>
                      )}
                      {template.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                      )}
                      
                      <div className="flex items-center text-xs text-gray-500 mb-4">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(template.created_at)}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTemplate(template)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // List View
                  <>
                    <div className="flex-shrink-0 w-16 h-12 bg-gray-100 rounded flex items-center justify-center mr-4">
                      {template.thumbnail_url ? (
                        <img 
                          src={template.thumbnail_url} 
                          alt={template.name}
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <FileText className="h-6 w-6 text-gray-400" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {editingId === template.id ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <input
                            ref={editInputRef}
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => handleRenameKeyDown(e, template.id)}
                            className="flex-1 text-sm font-semibold border border-blue-400 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                          />
                          <button onClick={() => saveRename(template.id)} className="text-green-600 hover:text-green-700"><Check className="h-4 w-4" /></button>
                          <button onClick={cancelEditing} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
                          <button onClick={e => startEditing(template, e)} title="Rename template" className="text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"><Edit className="h-3 w-3" /></button>
                        </div>
                      )}
                      {template.description && (
                        <p className="text-sm text-gray-600 truncate">{template.description}</p>
                      )}
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(template.created_at)}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}


      </div>
      </div>
  );

  return standalone ? content : <ScopingAILayout>{content}</ScopingAILayout>;
}
